import React from "react";
import {
    Box,
    BoxProps,
    Button,
    Card,
    CardProps,
    IconButton,
    TableContainerProps,
    Typography,
    Checkbox,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableRow,
    TableHead,
    useTheme,
    TextField,
    Chip,
} from "@mui/material";
import * as companyService from "services/company-service";
import * as Styles from "pages/development/settings/tabs/companies/listCompanies.styles";
import { useTranslation } from "react-i18next";
import EditIcon from "@mui/icons-material/Edit";
import EditModal from "./modals/editModal";
import CreateModal from "./modals/createModal";
import MergeCompaniesModal from "./modals/mergeCompaniesModal";

interface IProps {
    tenantId: string;
}

const ListCompanies = (props: IProps) => {
    const { tenantId } = props;
    const theme = useTheme();
    const { t } = useTranslation();
    const [showErrorMessage, setShowErrorMessage] =
        React.useState<boolean>(false);
    const [errorMessage, setErrorMessage] = React.useState<string>("");
    const [companies, setCompanies] = React.useState<Company[]>([]);
    const [isLoading, setIsLoading] = React.useState<boolean>(false);
    const [open, setOpen] = React.useState<{
        open: boolean;
        company: Company | null;
    }>({ open: false, company: null });
    const [createOpen, setCreateOpen] = React.useState<boolean>(false);
    const [mergeOpen, setMergeOpen] = React.useState<boolean>(false);
    const [selectedCompanies, setSelectedCompanies] = React.useState<Company[]>(
        [],
    );

    // const [rowCount, setRowCount] = React.useState<number>(0);
    // const [page, setPage] = React.useState<number>(0);
    // const [pageSize, setPageSize] = React.useState<number>(25);
    // const [direction] = React.useState<string>("");
    // const [sortOrder] = React.useState<string>("");

    const [companyName, setCompanyName] = React.useState<string>("");
    const [companyValidNames, setCompanyValidNames] =
        React.useState<string>("");

    React.useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [companyName, companyValidNames, tenantId]);

    const refreshMergeData = (): void => {
        setSelectedCompanies([]);
        fetchData();
    };

    const fetchData = (): void => {
        setIsLoading(true);
        setShowErrorMessage(false);
        companyService
            .getAll(
                0,
                2000000,
                "",
                "",
                companyName,
                companyValidNames,
                tenantId,
            )
            .then((res: PagedResponse<Company>) => {
                setCompanies(res.results);
                setIsLoading(false);
            })
            .catch((err) => {
                setErrorMessage(err.message);
                setShowErrorMessage(true);
                setIsLoading(false);
            });
    };

    const handleSelectCompanie = (company: Company): void => {
        if (selectedCompanies.includes(company)) {
            setSelectedCompanies(
                selectedCompanies.filter(
                    (companie) => companie.id !== company.id,
                ),
            );
        } else {
            setSelectedCompanies([...selectedCompanies, company]);
        }
        // if (selectedCompanies.map(c => c.id).includes(id)) {
        //     setSelectedCompanies(
        //         selectedCompanies.filter((companie) => companie.id !== id),
        //     );
        // } else {
        //     setSelectedCompanies([...selectedCompanies, id]);
        // }
    };

    const handleOpenCreate = () => {
        setCreateOpen(!createOpen);
    };

    const handleOpenMergeCompanies = (): void => {
        setMergeOpen(!mergeOpen);
    };

    const listCompaniesWrapperProps: CardProps = {
        sx: Styles.listRequestsWrapper(theme),
    };

    const titleWrapperProps: BoxProps = {
        sx: Styles.titleWrapper(theme),
    };

    const tableContainerProps: TableContainerProps = {
        sx: Styles.tableContainer(theme),
    };

    return (
        <Box>
            <Typography variant="h4">
                {t("settings.companies.title")}
            </Typography>
            <Box>{t("settings.companies.description")}</Box>
            <Card {...listCompaniesWrapperProps}>
                <Box {...titleWrapperProps}>
                    {showErrorMessage && (
                        <Box color="error.main">
                            <Box>{errorMessage}</Box>
                            <Button
                                sx={{ marginTop: "5px" }}
                                color="error"
                                variant="outlined"
                                onClick={() => {
                                    fetchData();
                                }}
                            >
                                {t("requests.errorButton")}
                            </Button>
                        </Box>
                    )}
                </Box>
                <Box {...titleWrapperProps}>
                    <Button
                        // {...buttonProps}
                        sx={{
                            marginTop: "1rem",
                            marginBottom: "1rem",
                        }}
                        variant="contained"
                        onClick={handleOpenCreate}
                    >
                        {t("company.newButton")}
                    </Button>
                    {/*<Button
                        // {...buttonProps}
                        sx={{
                            position: "absolute",
                            right: "0",
                            transform: "translateX(-90%)",
                            marginTop: "1rem",
                            marginBottom: "1rem",
                        }}
                        variant="contained"
                        onClick={handleOpenMergeCompanies}
                        disabled={selectedCompanies.length < 2}
                    >
                        {t("company.linkCompany")}
                    </Button>*/}
                </Box>
                <Box {...tableContainerProps}>
                    <TableContainer>
                        <Table
                            sx={{ minWidth: 700 }}
                            aria-label="customized table"
                            size="small"
                        >
                            <TableHead>
                                <TableRow>
                                    <TableCell />
                                    <TableCell>
                                        <TextField
                                            size="small"
                                            label={t("company.labels.name")}
                                            onChange={(e) => {
                                                setCompanyName(e.target.value);
                                            }}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <TextField
                                            size="small"
                                            label={t(
                                                "company.labels.validNames",
                                            )}
                                            onChange={(e) => {
                                                setCompanyValidNames(
                                                    e.target.value,
                                                );
                                            }}
                                        />
                                    </TableCell>
                                    <TableCell align="right">
                                        <Button
                                            variant="contained"
                                            onClick={handleOpenMergeCompanies}
                                            disabled={
                                                selectedCompanies.length < 2
                                            }
                                        >
                                            {t("company.linkCompany")}
                                        </Button>
                                    </TableCell>
                                    {/*{t("company.editButton")}*/}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {companies.map((companie) => (
                                    <TableRow
                                        hover
                                        key={companie.id}
                                        sx={{ backgroundColor: "white" }}
                                    >
                                        <TableCell component="th" scope="row">
                                            <Checkbox
                                                onChange={() =>
                                                    handleSelectCompanie(
                                                        companie,
                                                    )
                                                }
                                                checked={selectedCompanies.includes(
                                                    companie,
                                                )}
                                            ></Checkbox>
                                        </TableCell>
                                        <TableCell component="th" scope="row">
                                            {companie.name}
                                        </TableCell>
                                        <TableCell component="th" scope="row">
                                            {companie.children.length > 0 &&
                                                companie.children.map(
                                                    (c: Company) => (
                                                        <Chip
                                                            key={c.id}
                                                            sx={{
                                                                marginRight:
                                                                    "0.25rem",
                                                            }}
                                                            label={c.name}
                                                            color="primary"
                                                            variant="outlined"
                                                        />
                                                    ),
                                                )}
                                        </TableCell>
                                        <TableCell align="right">
                                            <IconButton
                                                onClick={() => {
                                                    setOpen({
                                                        open: true,
                                                        company: companie,
                                                    });
                                                }}
                                            >
                                                <EditIcon />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    {/*<DataTable
                        columns={[
                            {
                                field: "name",
                                headerName: t("company.labels.name"),
                                renderHeader: () => (
                                    <TextField
                                        label={t("company.labels.name")}
                                        value={companyName}
                                        onChange={(e) => {
                                            setCompanyName(e.target.value);
                                        }}
                                        size="small"
                                        sx={{
                                            marginTop: "0.50rem",
                                            backgroundColor: "white",
                                            marginBottom: "0.50rem",
                                        }}
                                    />
                                ),
                            },
                            {
                                field: "validNames",
                                headerName: t("company.labels.validNames"),
                                renderHeader: () => (
                                    <TextField
                                        label={t("company.labels.validNames")}
                                        value={companyValidNames}
                                        onChange={(e) => {
                                            setCompanyValidNames(
                                                e.target.value,
                                            );
                                        }}
                                        size="small"
                                        sx={{
                                            backgroundColor: "white",
                                            marginTop: "0.50rem",
                                            marginBottom: "0.50rem",
                                        }}
                                    />
                                ),
                                valueFormatter: (props) =>
                                    (props?.value as string[])?.join(", "),
                            },
                            {
                                field: "actions",
                                hideable: false,
                                renderHeader: () => <Box></Box>,
                                renderCell: (params) => (
                                    <Tooltip
                                        placement="top"
                                        title={t("company.editButton")}
                                    >
                                        <IconButton
                                            onClick={() => {
                                                setOpen({
                                                    open: true,
                                                    company:
                                                        params.row as Company,
                                                });
                                            }}
                                        >
                                            <EditIcon />
                                        </IconButton>
                                    </Tooltip>
                                ),
                            },
                        ]}
                        rows={companies}
                        rowCount={rowCount}
                        paginationModel={{ pageSize: pageSize, page: page }}
                        updatePagination={(params: GridPaginationModel) => {
                            setPageSize(params.pageSize);
                            setPage(params.page);
                        }}
                        loading={isLoading}
                        columnVisibilityModel={{}}
                        onColumnVisibilityModelChange={() => {}}
                        initialColumnVisibility={{}}
                        toolbar={[]}
                        reset={() => {}}
                        showToolbar={false}
                    />*/}
                </Box>
                <EditModal
                    open={open.open}
                    company={open.company}
                    handleClose={() => setOpen({ open: false, company: null })}
                    refresh={fetchData}
                    tenantId={tenantId}
                />
                <CreateModal
                    open={createOpen}
                    onClose={handleOpenCreate}
                    refresh={fetchData}
                    tenantId={tenantId}
                />
                <MergeCompaniesModal
                    open={mergeOpen}
                    onClose={handleOpenMergeCompanies}
                    selectedCompanies={selectedCompanies}
                    refresh={refreshMergeData}
                    tenantId={tenantId}
                />
            </Card>
        </Box>
    );
};

export default ListCompanies;
